// >>> CHERRY-PICKED from commit 0bbece4b4 (branch SM-2747-improve-image-upload): the Dialog/DialogContent/CloseIcon
// imports below, the enlargedMedia state, the img onClick, and the closing <Dialog> lightbox block are all from
// that commit. Drop this pick and take that branch's version of this file once it merges into this branch's target.
import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper, IconButton, Tooltip, Dialog, DialogContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { DragDropContext, Droppable, Draggable, DropResult } from 'react-beautiful-dnd';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import InfoIcon from '@mui/icons-material/Info';
import i18next from 'i18next';

interface MediaItem {
  id: string | number;
  name: string;
  previewUrl: string;
  logo?: boolean;
  offer?: boolean;
  outdoorView?: boolean;
  restaurant?: boolean;
  room?: boolean;
  seminarRoom?: boolean;
  homepage?: boolean;
}

interface SortableMediaTableProps {
  medias: MediaItem[];
  onReorder: (startIndex: number, endIndex: number) => void;
  onRemove: (index: number) => void;
  headers: React.ReactNode[];
  renderActions: (index: number) => React.ReactNode;
  /** Optional cell renderer for picture description (customer-facing caption). */
  renderDescription?: (index: number) => React.ReactNode;
  disabled?: boolean;
}

const getMediaFlagsTooltip = (item: MediaItem): string => {
  const flags = [
    { label: i18next.t('contentmedia-flag-logo'), value: item.logo },
    { label: i18next.t('contentmedia-flag-offer'), value: item.offer },
    { label: i18next.t('contentmedia-flag-outdoorView'), value: item.outdoorView },
    { label: i18next.t('contentmedia-flag-restaurant'), value: item.restaurant },
    { label: i18next.t('contentmedia-flag-room'), value: item.room },
    { label: i18next.t('contentmedia-flag-seminarRoom'), value: item.seminarRoom },
    { label: i18next.t('contentmedia-flag-homepage'), value: item.homepage },
  ];
  return flags.map(f => `${f.value ? '✓' : '✗'} ${f.label}`).join('\n');
};

/**
 * Drag-and-drop sortable table for media items.
 * Uses react-beautiful-dnd for reordering. Displays media flags in tooltip.
 */
export default function SortableMediaTable({
  medias,
  onReorder,
  onRemove,
  headers,
  renderActions,
  renderDescription,
  disabled = false,
}: SortableMediaTableProps) {
  const [enlargedMedia, setEnlargedMedia] = React.useState<MediaItem | null>(null);

  const handleDragEnd = (result: DropResult) => {
    if (disabled) return;
    if (!result.destination) return;

    const startIndex = result.source.index;
    const endIndex = result.destination.index;

    if (startIndex !== endIndex) {
      onReorder(startIndex, endIndex);
    }
  };

  return (
    <TableContainer component={Paper} sx={{ width: '100%' }}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: '50px' }}></TableCell>
            {headers.map((header, index) => (
              <TableCell key={`header_${index}`}>{header}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={handleDragEnd}>
          <Droppable droppableId="media-table">
            {(provided, snapshot) => (
              <TableBody
                {...provided.droppableProps}
                ref={provided.innerRef}
                sx={{
                  backgroundColor: snapshot.isDraggingOver ? '#f5f5f5' : 'inherit',
                }}
              >
                {medias.map((media, index) => (
                  <Draggable key={`media-${media.id}`} draggableId={`media-${media.id}`} index={index} isDragDisabled={disabled}>
                    {(provided, snapshot) => (
                      <TableRow
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          '&:nth-child(odd)': { backgroundColor: '#f9f9f9' },
                          backgroundColor: snapshot.isDragging ? '#e3f2fd' : 'inherit',
                          boxShadow: snapshot.isDragging ? '0 4px 8px rgba(0,0,0,0.2)' : 'none',
                          transform: snapshot.isDragging ? 'rotate(5deg)' : 'none',
                        }}
                      >
                        <TableCell sx={{ width: '50px', padding: '8px' }}>
                          <div {...provided.dragHandleProps}>
                            <DragIndicatorIcon sx={{ color: disabled ? 'grey.400' : '#666', cursor: disabled ? 'default' : 'grab' }} />
                          </div>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{media.name}</TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <img
                            src={media.previewUrl}
                            width={150}
                            alt={media.name}
                            style={{ cursor: 'pointer' }}
                            onClick={() => setEnlargedMedia(media)}
                          />
                        </TableCell>
                        {renderDescription && <TableCell sx={{ verticalAlign: 'top', minWidth: 220 }}>{renderDescription(index)}</TableCell>}
                        <TableCell sx={{ verticalAlign: 'top' }}>
                          <Tooltip title={<span style={{ whiteSpace: 'pre-line' }}>{getMediaFlagsTooltip(media)}</span>} arrow>
                            <IconButton size="small">
                              <InfoIcon fontSize="small" sx={{ color: 'grey.500' }} />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ verticalAlign: 'top' }}>{renderActions(index)}</TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
      </Table>
      <Dialog open={!!enlargedMedia} onClose={() => setEnlargedMedia(null)} maxWidth="lg">
        <IconButton
          onClick={() => setEnlargedMedia(null)}
          sx={{ position: 'absolute', top: 8, right: 8, backgroundColor: 'rgba(255,255,255,0.8)' }}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent sx={{ p: 0, lineHeight: 0 }}>
          {enlargedMedia && (
            <img src={enlargedMedia.previewUrl} alt={enlargedMedia.name} style={{ maxWidth: '90vw', maxHeight: '85vh', display: 'block' }} />
          )}
        </DialogContent>
      </Dialog>
    </TableContainer>
  );
}
